/**
 * Coluna DIREITA do /chats (300px, colapsável): dados do solicitante, o número
 * da empresa em que ele fala e as OUTRAS conversas dele (spec 028), vínculos de
 * cliente/departamento (PUT), responsável, ticket e avaliação.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Mail, MessageCircle, Phone, Smartphone, Ticket, UserRound } from 'lucide-react';
import { whatsappChannelLabel, type ChatDetail, type ChatRelatedItem } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { EmptyState, Separator, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { chatDetailOptions, chatRelatedOptions } from './chats-queries';
import { useUpdateChat } from './chats-mutations';
import {
  ChannelChip,
  ChatAvatar,
  Stars,
  StatusBadge,
  chatDisplayName,
  errorMessage,
  formatRelativeTime,
  formatWaPhone,
} from './chats-format';
import { LinkClientDialog, LinkTicketDialog } from './chat-dialogs';
import { ContactSheet } from './contact-sheet';

export function ChatInfoPanel({
  chatId,
  onSelectChat,
}: {
  chatId: number;
  /** abre outra conversa (spec 028: as da mesma pessoa) */
  onSelectChat: (chatId: number) => void;
}) {
  const detailQ = useQuery(chatDetailOptions(chatId));

  return (
    <div className="flex w-[300px] shrink-0 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="shrink-0 border-b px-4 py-2.5 text-sm font-semibold">Informações</div>
      {detailQ.isPending ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : detailQ.isError || !detailQ.data ? (
        <EmptyState message={`Erro: ${errorMessage(detailQ.error)}`} />
      ) : (
        <PanelBody chat={detailQ.data} onSelectChat={onSelectChat} />
      )}
    </div>
  );
}

function PanelBody({ chat, onSelectChat }: { chat: ChatDetail; onSelectChat: (chatId: number) => void }) {
  const update = useUpdateChat(chat.id);
  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active);
  const [dialog, setDialog] = useState<'client' | 'ticket' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // spec 056: o painel "Contato" (nome e e-mail editáveis)
  const [contactOpen, setContactOpen] = useState(false);

  const archived = chat.status === 'archived';

  const setDesk = (raw: string) => {
    setError(null);
    update.mutate(
      { deskId: raw === '' ? null : Number(raw) },
      { onError: (e) => setError(errorMessage(e)) }
    );
  };

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
      {/* Encerramento (spec 013): motivo, observação e quem encerrou. Some ao
          retomar — o registro anterior segue na conversa, como nota interna */}
      {archived && chat.closeReason ? (
        <>
          <section className="space-y-1.5">
            <SectionTitle>Encerramento</SectionTitle>
            <p className="text-sm">
              <span className="font-medium">{chat.closeReason.name}</span>
              {chat.closedBy ? (
                <span className="text-muted-foreground"> · por {chat.closedBy.name}</span>
              ) : null}
            </p>
            {chat.closeNote ? (
              <p className="text-sm break-words whitespace-pre-wrap text-muted-foreground">
                {chat.closeNote}
              </p>
            ) : null}
          </section>
          <Separator />
        </>
      ) : null}

      {/* Solicitante */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <SectionTitle>Solicitante</SectionTitle>
          <Button variant="ghost" size="xs" onClick={() => setContactOpen(true)}>
            Editar
          </Button>
        </div>
        <InfoRow icon={<UserRound className="size-3.5" />} label="Nome">
          {chatDisplayName(chat)}
        </InfoRow>
        <InfoRow icon={<Phone className="size-3.5" />} label="Telefone">
          {formatWaPhone(chat.requestorPhone) ?? '—'}
        </InfoRow>
        {/* contato WhatsApp (E.164) do canal — v6 */}
        {chat.waContact ? (
          <InfoRow icon={<MessageCircle className="size-3.5" />} label="WhatsApp">
            {formatWaPhone(chat.waContact)}
          </InfoRow>
        ) : null}
        {/* spec 028 (FR-003): o número da EMPRESA em que ele fala */}
        {chat.channel ? (
          <InfoRow icon={<Smartphone className="size-3.5" />} label="Número da empresa">
            {whatsappChannelLabel(chat.channel)}
          </InfoRow>
        ) : null}
        <InfoRow icon={<Mail className="size-3.5" />} label="E-mail">
          {chat.requestorEmail ?? '—'}
        </InfoRow>
      </section>

      {/* spec 028 (FR-002): as outras conversas da mesma pessoa */}
      {chat.origin === 'whatsapp' ? <RelatedChats chat={chat} onSelectChat={onSelectChat} /> : null}

      <Separator />

      {/* Cliente */}
      <section className="space-y-2">
        <SectionTitle>Cliente</SectionTitle>
        {chat.client ? (
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/clients/$clientId"
              params={{ clientId: String(chat.client.id) }}
              className="truncate text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
            >
              {chat.client.name}
            </Link>
            {!archived ? (
              <Button variant="ghost" size="xs" onClick={() => setDialog('client')}>
                Alterar
              </Button>
            ) : null}
          </div>
        ) : archived ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <Button variant="outline" size="xs" onClick={() => setDialog('client')}>
            Vincular cliente
          </Button>
        )}
      </section>

      <Separator />

      {/* Departamento (mesa) */}
      <section className="space-y-2">
        <SectionTitle>Departamento</SectionTitle>
        {archived ? (
          <p className="text-sm text-muted-foreground">{chat.desk?.name ?? '—'}</p>
        ) : (
          <Select
            aria-label="Departamento"
            className="h-8"
            value={chat.desk ? String(chat.desk.id) : ''}
            onChange={(e) => setDesk(e.target.value)}
            disabled={desksQ.isPending || update.isPending}
          >
            <option value="">Sem departamento</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.displayName}
              </option>
            ))}
          </Select>
        )}
      </section>

      <Separator />

      {/* Responsável */}
      <section className="space-y-2">
        <SectionTitle>Responsável</SectionTitle>
        {chat.responsible ? (
          <div className="flex items-center gap-2">
            <ChatAvatar name={chat.responsible.name} className="size-6 text-[10px]" />
            <span className="truncate text-sm">{chat.responsible.name}</span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Sem responsável</p>
        )}
      </section>

      <Separator />

      {/* Ticket */}
      <section className="space-y-2">
        <SectionTitle>Ticket</SectionTitle>
        {chat.ticketNumber !== null ? (
          <div className="min-w-0">
            <Link
              to="/tickets/$ticketNumber"
              params={{ ticketNumber: String(chat.ticketNumber) }}
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary-pressed"
            >
              <Ticket className="size-3.5" />#{chat.ticketNumber}
            </Link>
            {chat.ticketTitle ? (
              <p className="truncate text-xs text-muted-foreground">{chat.ticketTitle}</p>
            ) : null}
          </div>
        ) : archived ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <Button variant="outline" size="xs" onClick={() => setDialog('ticket')}>
            Vincular ticket
          </Button>
        )}
      </section>

      {/* Avaliação (somente encerrados com nota) */}
      {archived && chat.assessment !== null ? (
        <>
          <Separator />
          <section className="space-y-2">
            <SectionTitle>Avaliação</SectionTitle>
            <div className="flex items-center gap-2">
              <Stars value={chat.assessment} />
              <span className="text-sm text-muted-foreground">{chat.assessment}/5</span>
            </div>
          </section>
        </>
      ) : null}

      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {dialog === 'client' ? <LinkClientDialog chat={chat} onClose={() => setDialog(null)} /> : null}
      {dialog === 'ticket' ? <LinkTicketDialog chat={chat} onClose={() => setDialog(null)} /> : null}
      <ContactSheet chatId={chat.id} open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

/**
 * As outras conversas da MESMA pessoa (spec 028, FR-002): o telefone em outra
 * grafia (com/sem o nono dígito), noutro número da empresa, ou uma conversa
 * anterior — com o número da empresa, a data e a situação. Um clique abre.
 */
function RelatedChats({ chat, onSelectChat }: { chat: ChatDetail; onSelectChat: (chatId: number) => void }) {
  const relatedQ = useQuery(chatRelatedOptions(chat.id));
  const items = relatedQ.data ?? [];
  if (relatedQ.isPending || (relatedQ.isSuccess && items.length === 0)) return null;
  return (
    <>
      <Separator />
      <section className="space-y-2" aria-label="Outras conversas desta pessoa">
        <SectionTitle>Outras conversas desta pessoa</SectionTitle>
        {relatedQ.isError ? (
          <p className="text-xs text-destructive">{errorMessage(relatedQ.error)}</p>
        ) : (
          <ul className="space-y-1">
            {items.map((r) => (
              <li key={r.id}>
                <RelatedChatRow item={r} otherForm={r.waContact !== chat.waContact} onSelect={() => onSelectChat(r.id)} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function RelatedChatRow({
  item,
  otherForm,
  onSelect,
}: {
  item: ChatRelatedItem;
  /** o número em outra grafia (a Meta sem o nono dígito, por exemplo) */
  otherForm: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col gap-0.5 rounded-md border px-2.5 py-1.5 text-left transition-colors',
        'hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none'
      )}
    >
      <span className="flex items-center justify-between gap-2">
        {item.channel ? (
          <ChannelChip channel={item.channel} short className="text-xs text-foreground" />
        ) : (
          <span className="text-xs">WhatsApp</span>
        )}
        <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
          {formatRelativeTime(item.lastMessageAt ?? item.createdAt)}
        </span>
      </span>
      <span className="flex items-center gap-1.5">
        <StatusBadge status={item.status} canceled={item.canceled} className="px-1.5 py-0 text-[10px]" />
        {otherForm && item.waContact ? (
          <span className="truncate text-[11px] text-muted-foreground" title="O mesmo telefone em outra grafia">
            {formatWaPhone(item.waContact)}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
      {children}
    </h3>
  );
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm" title={label}>
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
}
